import type { AIAgent } from '@app/services/api.types';
import {
    Descriptions,
    Divider,
    Drawer,
    Empty,
    Flex,
    List,
    Tabs,
    type DescriptionsProps,
    type DrawerProps,
    type TabsProps,
} from 'antd';
import useAgent from '@app/hooks/agents/useAgent';
import { StyledButton } from '@app/components/button/Button';
import { useNavigate } from 'react-router';
import { url } from '@app/utils/constants';
import ReactMarkdown from 'react-markdown';
import { useState } from 'react';
import { BiLink } from 'react-icons/bi';

export type AgentDrawerProps = DrawerProps & {
    data: AIAgent | null;
};

const DEFAULT_TAB = 'details';

export const AgentDrawer = ({ data, ...props }: AgentDrawerProps) => {
    const navigate = useNavigate();
    const { data: agentData } = useAgent(
        { id: data?.id || '' },
        {
            enabled: Boolean(data?.id),
        }
    );

    const [activeTab, setActiveTab] = useState<string>(DEFAULT_TAB);
    const detailsItems: DescriptionsProps['items'] = [
        {
            key: 'name',
            label: 'Agent Name',
            children: data?.name || '-',
        },
        {
            key: 'voice',
            label: 'Agent Voice',
            children: data?.voice || '-',
        },
        {
            key: 'model',
            label: 'Agent Model',
            children: data?.model_name || '-',
        },
        {
            key: 'backend-model',
            label: 'Backend Model',
            children: data?.backend_model || '-',
        },
        {
            key: 'type',
            label: 'Type',
            children: data?.type_name || '-',
        },
        {
            key: 'assigned',
            label: 'Numbers Assigned to Agent',
            children: data?.numbers_count || 0,
            span: 2,
        },
    ];

    const numberItems: DescriptionsProps['items'][] =
        agentData?.numbers?.map((item, index) => [
            {
                key: `brand_${index}`,
                label: 'Brand',
                children: item.brand || '-',
            },
            {
                key: `location_${index}`,
                label: 'Location',
                children: item.location || '-',
            },
            {
                key: `number_${index}`,
                label: 'Agent Number',
                children: item.number || '-',
            },
            {
                key: `failover_${index}`,
                label: 'Failover Number',
                children: item.failover_number || '-',
            },
        ]) || [];

    const renderAssignedNumbersTab = () => {
        if (!numberItems.length) {
            return (
                <Empty description="No numbers assigned" image={Empty.PRESENTED_IMAGE_SIMPLE}>
                    <StyledButton
                        size="small"
                        icon={<BiLink />}
                        onClick={() => {
                            navigate(url.adminPortal.internalAdmin.assignAgent.numbers);
                        }}>
                        Assign Number
                    </StyledButton>
                </Empty>
            );
        }

        return numberItems?.map((items, index) => (
            <Descriptions
                key={index}
                colon={false}
                layout="vertical"
                items={items}
                column={2}
                className="fixed-cols numbers-assigned-item"
            />
        ));
    };

    const renderDocumentsPreview = () => {
        const documents = agentData?.documents ?? [];

        return (
            <>
                <Divider />
                <Descriptions title="Associated Documents" colon={false} layout="vertical" column={1}>
                    <Descriptions.Item>
                        {documents.length ? (
                            <List
                                size="small"
                                bordered
                                dataSource={documents}
                                renderItem={(doc) => <List.Item>{doc.filename}</List.Item>}
                            />
                        ) : (
                            <Empty
                                description="No documents uploaded"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        )}
                    </Descriptions.Item>
                </Descriptions>
            </>
        );
    };

    const renderDetailsTab = () => {
        return (
            <>
                <Descriptions
                    title="Details"
                    colon={false}
                    layout="vertical"
                    items={detailsItems}
                    column={2}
                />
                {data?.model_name === 'gpt-live-1' ? renderDocumentsPreview() : null}
            </>
        );
    };

    const renderPromptTab = (prompt = '') => {
        return (
            <>
                <Descriptions
                    colon={false}
                    layout="vertical"
                    items={[
                        {
                            key: '1',
                            children: (
                                <div className="markdown-body">
                                    <ReactMarkdown>{prompt || '-'}</ReactMarkdown>
                                </div>
                            ),
                        },
                    ]}
                />
            </>
        );
    };

    const items: TabsProps['items'] = [
        {
            key: 'details',
            label: 'Details',
            children: renderDetailsTab(),
        },
        ...(data?.model_name === 'gpt-live-1'
            ? [
                  {
                      key: 'fe-instructions',
                      label: 'Frontend Instructions',
                      children: renderPromptTab(data?.frontend_instructions),
                  },
                  {
                      key: 'be-instructions',
                      label: 'Backend Instructions',
                      children: renderPromptTab(data?.backend_instructions),
                  },
              ]
            : [
                  {
                      key: 'prompt',
                      label: 'Prompt',
                      children: renderPromptTab(data?.prompt),
                  },
              ]),
        {
            key: 'numbers-assigned',
            label: 'Numbers Assigned',
            children: renderAssignedNumbersTab(),
        },
    ];

    return (
        <Drawer
            title={
                <>
                    <Flex align="center" justify="space-between" gap={12}>
                        <span>AI Agent Details</span>
                    </Flex>
                    {data?.id ? (
                        <div className="ant-drawer-header-meta">
                            <span>ID: {data?.id}</span>
                        </div>
                    ) : null}
                </>
            }
            size={700}
            closable={{ 'aria-label': 'Close Button' }}
            afterOpenChange={() => {
                if (!props.open) {
                    setActiveTab(DEFAULT_TAB);
                }
            }}
            {...props}>
            <Tabs
                items={items}
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                className="sticky-tabs"
            />
        </Drawer>
    );
};

export default AgentDrawer;
